import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Devices as DevicesIcon,
  ArrowForward as NavigateIcon
} from '@mui/icons-material';
import CustomDataPanel from '../../../../../components/CustomDataPanel';
import LocationService from '../../../../../services/locationService';
import { getLocationId, findLocationById } from '../../../utils/locationTransformers';

const OverviewTab = ({
  selectedLocation,
  devices,
  hierarchicalLocations,
  onSelectLocation,
  setSelectedLocation,
  setLocations,
  t
}) => {
  const locationId = getLocationId(selectedLocation);

  const handleNavigateToParent = () => {
    const parent = findLocationById(hierarchicalLocations, selectedLocation.parentLocation);
    if (parent) onSelectLocation(parent);
  };

  const handleCustomDataSave = async (newData) => {
    try {
      await LocationService.updateLocation(locationId, { data: newData });

      const updatedLocation = { ...selectedLocation, data: newData };
      setSelectedLocation(updatedLocation);

      if (setLocations) {
        setLocations((prevLocations) => {
          const updateNode = (nodes) =>
            nodes.map((node) => {
              if (getLocationId(node) === locationId) {
                return { ...node, data: newData };
              }
              if (node.children) {
                return { ...node, children: updateNode(node.children) };
              }
              return node;
            });
          return updateNode(prevLocations);
        });
      }

      return true;
    } catch (error) {
      console.error('Error updating location data:', error);
      throw error;
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t ? t('LocationDetails.location_information') : 'Location Information'}
            </Typography>
            <List dense>
              {selectedLocation.parentLocation && (
                <ListItem
                  button
                  onClick={handleNavigateToParent}
                  sx={{ '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' } }}
                >
                  <ListItemIcon>
                    <LocationIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Parent Location"
                    secondary={selectedLocation.parentLocationName || 'Parent'}
                  />
                  <Tooltip title={t ? t('LocationManagement.navigate_parent_location') : 'Navigate to parent location'}>
                    <IconButton size="small" edge="end">
                      <NavigateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary="Type"
                  secondary={selectedLocation.locationTypeModel?.friendlyName || 'Unknown'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Created"
                  secondary={new Date(selectedLocation.createdAt || selectedLocation.created).toLocaleDateString()}
                />
              </ListItem>
              {selectedLocation.updatedAt && (
                <ListItem>
                  <ListItemText
                    primary="Last Updated"
                    secondary={new Date(selectedLocation.updatedAt).toLocaleDateString()}
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t ? t('LocationDetails.statistics') : 'Statistics'}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><DevicesIcon /></ListItemIcon>
                <ListItemText primary="Devices" secondary={`${devices.length} devices in this location`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationIcon /></ListItemIcon>
                <ListItemText primary="Sublocations" secondary={`${selectedLocation.children?.length || 0} direct sublocations`} />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      {selectedLocation.address && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t ? t('LocationManagement.address') : 'Address'}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {selectedLocation.address.street && `${selectedLocation.address.street}, `}
                {selectedLocation.address.city && `${selectedLocation.address.city}, `}
                {selectedLocation.address.state && `${selectedLocation.address.state} `}
                {selectedLocation.address.postalCode}
                {selectedLocation.address.country && `, ${selectedLocation.address.country}`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid item xs={12}>
        <CustomDataPanel
          title={t ? t('VisualTreeView.custom_location_data') : 'Custom Location Data'}
          data={selectedLocation.data || []}
          entityType="location"
          entityId={locationId}
          onSave={handleCustomDataSave}
        />
      </Grid>
    </Grid>
  );
};

export default OverviewTab;
